import { LLMResponse, SceneData } from '../../types';
import { GAME_CONFIG } from '../../config';

/**
 * 响应处理器，用于处理LLM响应并转换为场景数据
 */
export class ResponseProcessor {
  private debugMode: boolean;
  
  /**
   * 构造函数
   * @param debugMode 是否处于调试模式
   */
  constructor(debugMode: boolean = GAME_CONFIG.debugMode) {
    this.debugMode = debugMode;
  }
  
  /**
   * 处理LLM响应
   * @param data LLM响应数据
   * @returns 处理后的场景数据
   */
  processResponse(data: LLMResponse): SceneData | null {
    try {
      if (!data.choices?.[0]?.message?.content) {
        this.logDebug('模型响应没有内容', data);
        return null;
      }

      let content = data.choices[0].message.content;
      
      // 如果开启了调试模式，输出原始内容
      this.logDebug('原始内容:', content);

      try {
        // 尝试提取JSON
        const jsonContent = this.extractJson(content);
        
        // 如果开启了调试模式，输出提取的JSON内容
        this.logDebug('提取的JSON内容:', jsonContent);

        try {
          // 尝试解析JSON
          const parsed = JSON.parse(jsonContent);

          // 验证必要的字段
          if (!this.validateSceneData(parsed)) {
            this.logDebug('JSON格式验证失败，尝试修复', parsed);
            const fixedData = this.repairSceneData(parsed, content);
            if (!this.validateSceneData(fixedData)) {
              throw new Error('无法修复数据结构');
            }
            return this.buildStandardSceneData(fixedData);
          }

          // 构建标准化的场景数据
          const result = this.buildStandardSceneData(parsed);

          // 如果开启了调试模式，输出最终结果
          this.logDebug('最终处理结果:', result);

          return result;
        } catch (parseError) {
          this.logDebug('JSON解析失败', { error: String(parseError), jsonContent });
          
          // 如果JSON解析失败，尝试从文本内容构建场景
          if (content.length > 100) {
            return this.createTextBasedScene(content);
          } else {
            throw parseError; // 内容太短，可能是真正的错误
          }
        }
      } catch (extractError) {
        this.logDebug('JSON提取失败', { error: String(extractError) });
        
        // 如果提取JSON失败，尝试从文本内容构建场景
        return this.createTextBasedScene(content);
      }
    } catch (error) {
      console.error('响应处理错误:', error);
      console.error('原始内容:', data.choices?.[0]?.message?.content || '无内容');
      
      // 返回错误场景作为最后的备选
      return this.createErrorScene();
    }
  }
  
  /**
   * 提取JSON内容
   * @param content 原始内容
   * @returns 提取的JSON字符串
   */
  private extractJson(content: string): string {
    this.logDebug('尝试提取JSON', { contentLength: content.length });
    
    // 首先尝试找到标准的JSON代码块
    const jsonBlockMatches = [
      // 标准的JSON代码块
      content.match(/```json\s*\n([\s\S]*?)\n\s*```/),
      // 其他代码块可能包含JSON
      content.match(/```\s*\n([\s\S]*?)\n\s*```/),
      // 明确的JSON对象，优先匹配包含scene_text的完整对象
      content.match(/\{\s*"scene_text"[\s\S]*?"choices"\s*:\s*\[[\s\S]*?\]\s*\}/),
      // 任何看起来像JSON的内容
      content.match(/\{[\s\S]*\}/)
    ];
    
    // 使用第一个匹配成功的结果
    for (const match of jsonBlockMatches) {
      if (match) {
        const extracted = match[1] || match[0];
        this.logDebug('成功提取JSON', { 
          source: match === jsonBlockMatches[0] ? 'json代码块' : 
                  match === jsonBlockMatches[1] ? '普通代码块' : 
                  match === jsonBlockMatches[2] ? 'scene_text对象' : '一般JSON对象',
          length: extracted.length 
        });
        
        // 尝试验证JSON结构
        try {
          JSON.parse(extracted);
          return extracted; // 如果是有效的JSON，直接返回
        } catch (e) {
          // 继续尝试其他匹配或修复
          this.logDebug('提取的JSON无效，将尝试修复', { error: String(e) });
        }
      }
    }
    
    // 如果没有明确的JSON标记，但内容中包含关键字段，尝试重建JSON
    if (content.includes('"scene_text"') && content.includes('"choices"')) {
      this.logDebug('找到关键JSON字段但格式不完整，尝试重建', { content: content.substring(0, 100) + '...' });
      // 使用更灵活的方式提取关键部分
      const sceneTextMatch = content.match(/"scene_text"\s*:\s*"([^"]*)"/);
      const choicesMatch = content.match(/"choices"\s*:\s*\[([\s\S]*?)(\]|$)/);
      
      if (sceneTextMatch) {
        let rebuilt = '{\n';
        rebuilt += `  "scene_text": "${sceneTextMatch[1]}",\n`;
        
        // 尝试提取NPC对话
        const npcDialogueMatch = content.match(/"npc_dialogue"\s*:\s*"([^"]*)"/);
        if (npcDialogueMatch) {
          rebuilt += `  "npc_dialogue": "${npcDialogueMatch[1]}",\n`;
        }
        
        // 添加choices
        rebuilt += '  "choices": [\n';
        if (choicesMatch && choicesMatch[1]) {
          // 清理并添加选项内容
          const choicesContent = choicesMatch[1].trim();
          rebuilt += choicesContent.endsWith(',') ? 
            choicesContent.substring(0, choicesContent.length - 1) : 
            choicesContent;
        } else {
          // 添加默认选项
          rebuilt += '    {\n';
          rebuilt += '      "text": "继续（选项生成错误，可以继续、手动输入或者更换模型）",\n';
          rebuilt += '      "next_scene": "continue",\n';
          rebuilt += '      "requirements": null\n';
          rebuilt += '    }';
        }
        rebuilt += '\n  ]\n}';
        
        try {
          JSON.parse(rebuilt);
          this.logDebug('成功重建JSON', { rebuilt });
          return rebuilt;
        } catch (e) {
          this.logDebug('重建的JSON仍然无效', { error: String(e), rebuilt });
        }
      }
    }
    
    // 如果没有明确的JSON标记，尝试修复并返回原始内容
    this.logDebug('无法提取有效JSON，将尝试修复原始内容', { contentLength: content.length });
    return this.fixBrokenJson(content);
  }
  
  /**
   * 验证场景数据
   * @param data 解析后的数据
   * @returns 是否是有效的场景数据
   */
  private validateSceneData(data: any): boolean {
    // 基本结构验证
    if (!data || typeof data !== 'object') return false;
    
    // 必要字段验证
    if (typeof data.scene_text !== 'string') return false;
    if (!Array.isArray(data.choices)) return false;
    
    // 选项验证
    if (data.choices.length === 0) return false;
    
    // 至少一个选项应该有文本和下一个场景ID
    return data.choices.some((choice: any) => 
      typeof choice.text === 'string' && 
      typeof choice.next_scene === 'string');
  }
  
  /**
   * 修复不完整的场景数据
   * @param data 不完整的数据
   * @param originalContent 原始内容
   * @returns 修复后的数据
   */
  private repairSceneData(data: any, originalContent: string): any {
    const repaired: any = {
      ...data
    };
    
    // 确保有场景文本
    if (!repaired.scene_text || typeof repaired.scene_text !== 'string') {
      // 尝试从原始内容中提取合理的文本
      const possibleText = originalContent
        .replace(/```[\s\S]*?```/g, '') // 移除代码块
        .replace(/\{[\s\S]*\}/g, '')    // 移除JSON对象
        .trim();
      
      repaired.scene_text = possibleText || "系统无法解析场景描述";
    }
    
    // 确保有选项
    if (!Array.isArray(repaired.choices) || repaired.choices.length === 0) {
      repaired.choices = [{
        text: "继续（选项生成错误，可以继续、手动输入或者更换模型）",
        next_scene: "continue",
        requirements: null
      }];
    } else {
      // 修复选项
      repaired.choices = repaired.choices.map((choice: any) => ({
        text: typeof choice.text === 'string' ? choice.text : "未知选项",
        next_scene: typeof choice.next_scene === 'string' ? choice.next_scene : "continue",
        requirements: choice.requirements || null
      }));
    }
    
    return repaired;
  }
  
  /**
   * 构建标准化的场景数据
   * @param parsed 解析后的数据
   * @returns 标准化的场景数据
   */
  private buildStandardSceneData(parsed: any): SceneData {
    return {
      scene_text: String(parsed.scene_text || ''),
      npc_dialogue: parsed.npc_dialogue ? 
        (typeof parsed.npc_dialogue === 'string' ? 
          String(parsed.npc_dialogue) : 
          parsed.npc_dialogue) : 
        undefined,
      choices: Array.isArray(parsed.choices) ? 
        parsed.choices.map((choice: any) => ({
          text: String(choice.text || ''),
          next_scene: String(choice.next_scene || ''),
          requirements: choice.requirements === 'null' || 
                        choice.requirements === undefined || 
                        choice.requirements === '' ? 
            null : choice.requirements
        })) : [],
      state_changes: parsed.state_changes || undefined
    };
  }
  
  /**
   * 从纯文本创建场景
   * @param content 纯文本内容
   * @returns 场景数据
   */
  private createTextBasedScene(content: string): SceneData {
    // 尝试提取一些有意义的文本
    const cleanedText = content
      .replace(/```[\s\S]*?```/g, '')  // 移除代码块
      .replace(/\{[\s\S]*\}/g, '')     // 移除JSON对象
      .trim();
    
    // 如果提取的文本太短，使用错误信息
    const sceneText = cleanedText.length > 30 ? 
      cleanedText : 
      "系统遇到了一些问题，需要重新生成对话。";
    
    return {
      scene_text: sceneText,
      npc_dialogue: "解析AI响应时遇到问题，但我们提取了有用的信息。",
      choices: [{
        text: "重新尝试",
        next_scene: "retry",
        requirements: null
      }]
    };
  }
  
  /**
   * 创建错误场景
   * @returns 错误场景数据
   */
  private createErrorScene(): SceneData {
    return {
      scene_text: "系统遇到了一些问题，需要重新生成对话。",
      npc_dialogue: "发生错误：无法解析AI响应。",
      choices: [{
        text: "重新尝试",
        next_scene: "retry",
        requirements: null
      }]
    };
  }
  
  /**
   * 修复损坏的JSON
   * @param jsonStr JSON字符串
   * @returns 修复后的JSON字符串
   */
  private fixBrokenJson(jsonStr: string): string {
    if (!jsonStr) return jsonStr;
    
    // 去除所有 null 字符串
    let fixedJson = jsonStr.replace(/null/g, '');
    
    try {
      // 尝试直接解析
      JSON.parse(fixedJson);
      return fixedJson;
    } catch (e) {
      this.logDebug('JSON解析错误，尝试修复', e);
      
      // 提取 JSON 结构
      const jsonMatch = fixedJson.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        fixedJson = jsonMatch[0];
      } else {
        return '{}'; // 如果找不到有效的 JSON 结构，返回空对象
      }
      
      // 尝试修复不完整的 choices 数组
      fixedJson = this.fixChoicesArray(fixedJson);
      
      // 用正则表达式查找并修复常见问题
      fixedJson = fixedJson
        // 处理 "key": 后面跟逗号或大括号的情况
        .replace(/"([^"]+)"\s*:\s*([,\}])/g, '"$1": null$2')
        // 处理 "key": 后面什么都没有的情况
        .replace(/"([^"]+)"\s*:\s*$/g, '"$1": null')
        // 处理缺少引号的 key
        .replace(/([{,]\s*)(\w+)(\s*:)/g, '$1"$2"$3')
        // 删除尾部多余的逗号
        .replace(/,(\s*[\]}])/g, '$1')
        // 修复缺少引号的字符串值
        .replace(/:\s*([^"{}\[\],\s][^{}\[\],]*[^"{}\[\],\s])(\s*[,}])/g, ':"$1"$2')
        // 修复布尔值和数字
        .replace(/:\s*true\s*([,}])/g, ':true$1')
        .replace(/:\s*false\s*([,}])/g, ':false$1')
        .replace(/:\s*(\d+)\s*([,}])/g, ':$1$2');
      
      try {
        // 尝试解析修复后的 JSON
        JSON.parse(fixedJson);
        return fixedJson;
      } catch (e) {
        this.logDebug('第一次修复失败，尝试更深层次修复', fixedJson);
        
        // 如果仍然无法解析，尝试进一步修复
        try {
          // 再次尝试修复不完整的 choices 数组
          fixedJson = this.fixChoicesArray(fixedJson, true);
          
          // 验证修复后的 JSON
          JSON.parse(fixedJson);
          return fixedJson;
        } catch (e) {
          // 如果仍然无法解析，构建一个有效的最小JSON
          return this.buildMinimalJson(fixedJson);
        }
      }
    }
  }
  
  /**
   * 修复不完整的 choices 数组
   * @param jsonStr JSON字符串
   * @param aggressive 是否使用激进修复模式
   * @returns 修复后的JSON字符串
   */
  private fixChoicesArray(jsonStr: string, aggressive: boolean = false): string {
    // 查找 "choices": [ 开始的数组
    const choicesMatch = jsonStr.match(/"choices"\s*:\s*\[([\s\S]*?)(\]|$)/);
    if (!choicesMatch) return jsonStr;
    
    const choicesContent = choicesMatch[1];
    const hasClosingBracket = choicesMatch[0].endsWith(']');
    
    // 如果没有闭合的方括号，添加一个
    if (!hasClosingBracket) {
      this.logDebug('choices 数组未闭合，添加闭合括号', { choicesContent });
      return jsonStr.replace(/"choices"\s*:\s*\[([\s\S]*?)$/, '"choices": [$1]');
    }
    
    // 检查和修复不完整的数组项
    if (aggressive || choicesContent.includes('{') && !choicesContent.match(/\}\s*\]/)) {
      this.logDebug('发现不完整的 choice 项，尝试修复', { choicesContent });
      
      // 拆分成单独的对象
      const items = choicesContent.split('},');
      
      // 修复和重组
      let fixedChoices = items.map((item, index) => {
        // 最后一项可能没有逗号
        const isLastItem = index === items.length - 1;
        
        // 如果项是空的或只有空白，跳过
        if (!item.trim()) return '';
        
        // 检查是否是完整对象 (有开始的 { 和结束的 })
        if (item.trim().startsWith('{') && item.trim().endsWith('}')) {
          return isLastItem ? item : item + ',';
        }
        
        // 检查是否缺少结束括号
        if (item.trim().startsWith('{') && !item.trim().endsWith('}')) {
          // 构建一个有效的对象
          let fixedItem = item;
          
          // 确保至少有 text 和 next_scene 字段
          if (!fixedItem.includes('"next_scene"')) {
            if (fixedItem.endsWith(',')) {
              // 如果以逗号结尾，添加字段
              fixedItem = fixedItem + ' "next_scene": "continue", "requirements": null';
            } else {
              // 如果不以逗号结尾，可能需要先添加逗号
              const lastProp = fixedItem.match(/"([^"]+)"\s*:\s*"([^"]+)"/);
              if (lastProp) {
                fixedItem = fixedItem.replace(
                  new RegExp(`"${lastProp[1]}"\\s*:\\s*"${lastProp[2]}"\\s*$`), 
                  `"${lastProp[1]}": "${lastProp[2]}", "next_scene": "continue", "requirements": null`
                );
              } else {
                // 如果无法找到最后的属性，简单地附加
                fixedItem = fixedItem + ', "next_scene": "continue", "requirements": null';
              }
            }
          }
          
          // 闭合对象
          fixedItem = fixedItem + '}';
          
          return isLastItem ? fixedItem : fixedItem + ',';
        }
        
        // 其他情况，返回原样
        return isLastItem ? item : item + ',';
      }).filter(Boolean).join('');
      
      // 替换原始的 choices 内容
      return jsonStr.replace(/"choices"\s*:\s*\[([\s\S]*?)(\]|$)/, `"choices": [${fixedChoices}]`);
    }
    
    return jsonStr;
  }
  
  /**
   * 构建最小的有效JSON
   * @param content 原始内容
   * @returns 有效的JSON字符串
   */
  private buildMinimalJson(content: string): string {
    // 尝试提取场景文本和NPC对话
    const sceneTextMatch = content.match(/"scene_text"\s*:\s*"([^"]*)"/);
    const npcDialogueMatch = content.match(/"npc_dialogue"\s*:\s*(?:"([^"]*)"|(\{[\s\S]*?\}))/);
    
    // 尝试从纯文本提取合理的描述
    const textDescription = content
      .replace(/```[\s\S]*?```/g, '')
      .replace(/\{[\s\S]*\}/g, '')
      .replace(/scene_text|npc_dialogue|choices|state_changes/g, '')
      .replace(/[{}[\]"]/g, '')
      .trim();
    
    // 手动构建有效的 JSON
    let result = '{\n';
    result += `    "scene_text": ${sceneTextMatch ? 
      `"${sceneTextMatch[1]}"` : 
      textDescription ? 
        `"${textDescription.substring(0, 200)}"` : 
        '"系统生成的场景描述"'},\n`;
    
    if (npcDialogueMatch) {
      if (npcDialogueMatch[1]) {  // 字符串类型
        result += `    "npc_dialogue": "${npcDialogueMatch[1]}",\n`;
      } else if (npcDialogueMatch[2]) {  // 对象类型
        result += `    "npc_dialogue": ${npcDialogueMatch[2]},\n`;
      }
    } else {
      result += '    "npc_dialogue": "",\n';
    }
    
    result += '    "choices": [\n';
    result += '        {\n';
    result += '            "text": "继续（选项生成错误，可以继续、手动输入或者更换模型）",\n';
    result += '            "next_scene": "continue",\n';
    result += '            "requirements": null\n';
    result += '        }\n';
    result += '    ],\n';
    result += '    "state_changes": {}\n';
    result += '}';
    
    return result;
  }
  
  /**
   * 记录调试信息
   * @param message 消息
   * @param data 数据
   */
  private logDebug(message: string, data: any): void {
    if (this.debugMode) {
      console.log(message, data);
    }
  }
} 